import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { Helmet } from "react-helmet-async";
import { StatCard } from "@/components/admin/stats/StatCard";
import { SubscriptionStats } from "@/components/admin/stats/SubscriptionStats";
import { UserManagementDialog } from "@/components/admin/users/UserManagementDialog";
import { EditUserDialog } from "@/components/admin/users/EditUserDialog";
import { ConfirmationDialogs } from "@/components/admin/users/ConfirmationDialogs";
import { useAdminUsers } from "@/components/admin/hooks/useAdminUsers";
import type { AdminStats, AdminUser } from "@/components/admin/types";

export default function Dashboard() {
  const [showUsersDialog, setShowUsersDialog] = useState(false);
  const [userFilter, setUserFilter] = useState<{ fn: (u: AdminUser) => boolean; label: string } | null>(null);
  const [editUser, setEditUser] = useState<AdminUser | null>(null);

  const openFiltered = (label: string, fn: (u: AdminUser) => boolean) => {
    setUserFilter({ fn, label });
    setShowUsersDialog(true);
  };

  const {
    users,
    usersLoading,
    roleChangeConfirm,
    accessToggleConfirm,
    handleRoleChange,
    confirmRoleChange,
    handleAccessToggle,
    confirmAccessToggle,
    setRoleChangeConfirm,
    setAccessToggleConfirm,
    isUpdating,
  } = useAdminUsers(showUsersDialog);

  const { data: stats, isLoading: statsLoading } = useQuery<AdminStats>({
    queryKey: ["/api/admin/stats"],
  });

  return (
    <>
      <Helmet>
        <title>Dashboard | ChefAIQ Admin</title>
      </Helmet>

      <div className="space-y-8">
        {/* Desktop header */}
        <div className="hidden md:block">
          <h1 className="text-4xl font-bold mb-2">Admin Dashboard</h1>
          <p className="text-muted-foreground">Platform overview and analytics</p>
        </div>

        {/* Mobile header */}
        <div className="md:hidden">
          <h1 className="text-2xl font-bold">Admin Dashboard</h1>
        </div>

        <div className="space-y-6">
          {/* Stat cards */}
          <div className="grid grid-cols-3 gap-4">
            {statsLoading ? (
              <>
                <Skeleton className="h-24" />
                <Skeleton className="h-24" />
                <Skeleton className="h-24" />
              </>
            ) : stats ? (
              <>
                <StatCard
                  label="Total Users"
                  value={stats.totalUsers}
                  onClick={() => { setUserFilter(null); setShowUsersDialog(true); }}
                />
                <StatCard
                  label="Online Now"
                  value={stats.onlineUsers || 0}
                  subtext={`${Math.round(((stats.onlineUsers || 0) / Math.max(stats.totalUsers, 1)) * 100)}% of total`}
                  onClick={() =>
                    openFiltered(
                      "Online Now",
                      (u) => (stats.onlineUserIds || []).includes((u as any).userId || u.id),
                    )
                  }
                />
                <StatCard
                  label="New Today"
                  value={stats.newSignupsToday}
                  onClick={() => {
                    const now = new Date();
                    const startOfDay = new Date(
                      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
                    );
                    openFiltered("New Today", (u) => {
                      if (!u.createdAt) return false;
                      return new Date(u.createdAt) >= startOfDay;
                    });
                  }}
                />
              </>
            ) : null}
          </div>

          {/* Subscription stats */}
          <div className="grid gap-4">
            <SubscriptionStats
              stats={stats}
              onPlanClick={(label, filterFn) => openFiltered(label, filterFn)}
            />
          </div>
        </div>

        {/* Dialogs */}
        <UserManagementDialog
          open={showUsersDialog}
          onOpenChange={(open) => {
            setShowUsersDialog(open);
            if (!open) setUserFilter(null);
          }}
          users={users}
          totalUsers={stats?.totalUsers || 0}
          filter={userFilter?.fn}
          filterLabel={userFilter?.label}
          isLoading={usersLoading}
          onRoleChange={handleRoleChange}
          onAccessToggle={handleAccessToggle}
          onEditUser={setEditUser}
          isUpdating={isUpdating}
        />

        <EditUserDialog
          open={!!editUser}
          onOpenChange={(open) => !open && setEditUser(null)}
          user={editUser}
        />

        <ConfirmationDialogs
          roleChangeConfirm={roleChangeConfirm}
          accessToggleConfirm={accessToggleConfirm}
          onRoleChangeConfirm={confirmRoleChange}
          onAccessToggleConfirm={confirmAccessToggle}
          onRoleChangeCancel={() => setRoleChangeConfirm(null)}
          onAccessToggleCancel={() => setAccessToggleConfirm(null)}
          isUpdating={isUpdating}
        />
      </div>
    </>
  );
}
